import type { Company } from "@domain/CRM/company";
import type { Deal } from "@domain/CRM/deal";
import { isRelevantSeason } from "@domain/CRM/deal";
import type { Job } from "@domain/CRM/job";
import { err, ok, type Result } from "@domain/Result";
import { runPooled } from "@domain/Utilities";
import type { IDeals } from "@ports/deals/IDeals";
import type { IGrowers } from "@ports/growers/IGrowers";
import type { IJobs } from "@ports/jobs/IJobs";
import type { IJobIssues } from "@ports/jobs/IJobIssues";

export interface DealMatcherUseCasesDeps {
	jobIssuesApi: IJobIssues;
	jobsApi: Pick<IJobs, "updateField">;
	dealsApi: IDeals;
	growersApi: IGrowers;
	/** Defaults to `() => new Date()`; tests inject a fixed clock. */
	now?: () => Date;
}

type DealScope = "company" | "season" | "all";

interface LoadDealMatcherInput {
	scope: DealScope;
	includeInactive: boolean;
	/** Required when `scope === "company"`; ignored otherwise. `null`/`undefined`
	 *  in `"company"` scope returns an empty deals list. */
	selectedCompany: Company | null | undefined;
}

interface DealMatcherJobsResult {
	jobs: Job[];
	/** Map from grower-id to their `samplingType` (empty string = fetched but
	 *  has no samplingType, used as a "don't refetch" sentinel). */
	growerSamplingTypeByGrowerId: Map<string, string>;
}

interface DealMatcherLoadResult extends DealMatcherJobsResult {
	deals: Deal[];
}

/** How many auto-match requests run at once. Each holds open until its lambda run has written the job's link
 *  (seconds), so this bounds both the lambda fan-out and the API's shared thread pool. */
const AUTO_MATCH_CONCURRENCY = 4;

/** A job whose auto-match run failed, and why. */
interface AutoMatchFailure {
	jobId: string;
	error: unknown;
}

interface ApplyAssignmentsInput {
	/** `Map<jobId, dealId>` — `dealId` is either a real Airtable id or a
	 *  synthetic `manual-<uuid>` for staged manual deals. */
	pendingAssignments: ReadonlyMap<string, string>;
	/** Manual deals staged in the UI. Each carries a synthetic `manual-<uuid>`
	 *  id; the use case persists them, gets back real ids, then rewrites the
	 *  matching pendingAssignments before linking them to jobs. */
	manualDeals: Deal[];
}

type ApplyAssignmentsError =
	| { kind: "deal_count_mismatch"; expected: number; actual: number }
	| { kind: "deal_missing_id"; index: number }
	| { kind: "missing_real_id_for_synthetic"; syntheticId: string };

/**
 * Drives the Admin → Deal Matcher page. Three workflows:
 *
 *  - `loadIssues({ scope, includeInactive, selectedCompany })` — fetches
 *    the unmatched jobs, maintains an internal grower→samplingType cache
 *    (with partial-fail tolerance: a failed grower fetch leaves the cache
 *    untouched so the next load retries), and fetches the candidate deals
 *    via one of three branching strategies:
 *      • `"all"` → every deal where `isConfigOnly === false`.
 *      • `"season"` → same filter, then post-filter via `isRelevantSeason`
 *        against the injected `now`.
 *      • `"company"` → all deals for the selected company, post-filtered
 *        by `isActive` (when `includeInactive` is false) and `isRelevantSeason`.
 *    Empty `[]` is returned when scope is `"company"` but no company is selected.
 *
 *  - `loadJobs()` — just the unmatched jobs (and the grower cache), for a
 *    refresh that leaves the deals alone.
 *
 *  - `fix(ids, onProgress)` — re-run the Lambda auto-matcher, one job per
 *    POST to /jobs/issues/deal-matcher, a few at a time. Each POST answers
 *    once the link is written, so the jobs can be refetched as soon as this
 *    resolves. Reports how many have finished, and resolves to the failures.
 *
 *  - `applyAssignments({ pendingAssignments, manualDeals })` — the critical
 *    correctness path. Returns a `Result` because there are real failure
 *    modes ops need to see vs unrecoverable thrown errors.
 *
 * The grower-sampling-type cache lives in the factory closure so it survives
 * across loadIssues calls within one bound instance — exactly the same
 * lifecycle the previous class instance had.
 */
export interface DealMatcherUseCases {
	loadIssues(input: LoadDealMatcherInput): Promise<DealMatcherLoadResult>;
	loadJobs(): Promise<DealMatcherJobsResult>;
	fix(ids: string[], onProgress?: (done: number) => void): Promise<AutoMatchFailure[]>;
	loadDealsByIds(ids: string[]): Promise<Deal[]>;
	applyAssignments(input: ApplyAssignmentsInput): Promise<Result<void, ApplyAssignmentsError>>;
}

export function makeDealMatcherUseCases(deps: DealMatcherUseCasesDeps): DealMatcherUseCases {
	const growerSamplingTypeCache = new Map<string, string>();
	const now = deps.now ?? (() => new Date());

	async function refreshGrowerCache(jobs: Job[]): Promise<void> {
		const uniqueGrowerIds = [...new Set(jobs.flatMap((j) => j.growerIds || []))];
		const idsToFetch = uniqueGrowerIds.filter((id) => !growerSamplingTypeCache.has(id));
		if (idsToFetch.length === 0) return;
		try {
			const growers = await deps.growersApi.getByIds(idsToFetch);
			// Mark every requested id first; empty string = fetched-but-no-samplingType,
			// the sentinel that prevents repeat fetches for growers with the field unset.
			for (const id of idsToFetch) growerSamplingTypeCache.set(id, "");
			for (const g of growers) growerSamplingTypeCache.set(g.id, g.samplingType ?? "");
		} catch {
			// Leave the cache untouched so the next loadIssues() retries these ids.
		}
	}

	async function fetchDeals(input: LoadDealMatcherInput): Promise<Deal[]> {
		const nowDate = now();
		if (input.scope === "all" || input.scope === "season") {
			const filter: Record<string, unknown> = { isConfigOnly: false };
			if (!input.includeInactive) filter.isActive = true;
			const allDeals = await deps.dealsApi.getByFilter({ fields: filter } as never);
			return input.scope === "season" ? allDeals.filter((d) => isRelevantSeason(d.season, nowDate)) : allDeals;
		}
		if (!input.selectedCompany) return [];
		const companyDeals = await deps.dealsApi.getByCompany(input.selectedCompany);
		const filtered = input.includeInactive ? companyDeals : companyDeals.filter((d) => d.isActive);
		return filtered.filter((d) => isRelevantSeason(d.season, nowDate));
	}

	async function loadJobs(): Promise<DealMatcherJobsResult> {
		const jobs = await deps.jobIssuesApi.getDealMatcherJobs();
		await refreshGrowerCache(jobs);
		return { jobs, growerSamplingTypeByGrowerId: new Map(growerSamplingTypeCache) };
	}

	return {
		async loadIssues(input) {
			const loaded = await loadJobs();
			const deals = await fetchDeals(input);
			return { ...loaded, deals };
		},

		loadJobs,

		async fix(ids, onProgress) {
			let done = 0;
			const outcomes = await runPooled(ids, AUTO_MATCH_CONCURRENCY, async (id) => {
				try {
					await deps.jobIssuesApi.fixDealMatcherJobs([id]);
				} finally {
					onProgress?.(++done);
				}
			});
			return outcomes.flatMap((o) => (o.ok ? [] : [{ jobId: o.item, error: o.error }]));
		},

		/** Replaces the candidate deals list with a hand-picked subset. The vm
		 *  uses this when the user pastes deal-ids into the search bar — the
		 *  scope-based default loadIssues() can't find them otherwise. */
		async loadDealsByIds(ids) {
			return deps.dealsApi.getByIds(ids);
		},

		async applyAssignments(input) {
			const idMap = new Map<string, string>();

			if (input.manualDeals.length > 0) {
				const payloads = input.manualDeals.map(({ id: _id, ...rest }) => rest as Partial<Deal>);
				const created = await deps.dealsApi.createMany(payloads);
				if (created.length !== input.manualDeals.length) {
					return err({ kind: "deal_count_mismatch", expected: input.manualDeals.length, actual: created.length });
				}
				for (let i = 0; i < input.manualDeals.length; i++) {
					const realId = created[i]?.id;
					if (!realId) return err({ kind: "deal_missing_id", index: i });
					idMap.set(input.manualDeals[i]!.id, realId);
				}
			}

			const entries: (readonly [string, string])[] = [];
			for (const [jobId, dealId] of input.pendingAssignments) {
				if (dealId.startsWith("manual-")) {
					const realId = idMap.get(dealId);
					if (!realId) return err({ kind: "missing_real_id_for_synthetic", syntheticId: dealId });
					entries.push([jobId, realId]);
				} else {
					entries.push([jobId, dealId]);
				}
			}

			if (entries.length > 0) {
				await Promise.all(entries.map(([jobId, dealId]) => deps.jobsApi.updateField(jobId, "dealIds", [dealId])));
			}
			return ok(undefined);
		},
	};
}
